import { useMutation } from '@tanstack/react-query';
import constate from 'constate';
import { useMemo, useState } from 'react';

import { api, parseError, useToast } from 'common';

import { useFlowDefinitionContext } from '../../../context';
import { type ReactFlowNode, type ReactFlowProps, isBlockNode } from '../../../types';

export enum FlowRunFollowMode {
  None = 'None',
  FollowActiveBlockDuringRun = 'FollowActiveBlockDuringRun',
}

interface UseFlowRunProps {
  flowRun: api.FlowRun.FlowRunWithData;
}

function useFlowRun(props: UseFlowRunProps) {
  const { reactFlowNodesAndEdges } = useFlowDefinitionContext();
  const { flowRun } = props;
  const toast = useToast();
  const [flowRunFollowMode, setFlowRunFollowMode] = useState(
    FlowRunFollowMode.FollowActiveBlockDuringRun
  );
  const { nodes, edges }: ReactFlowProps = useMemo(() => {
    const { nodes, edges } = reactFlowNodesAndEdges;
    return {
      nodes: nodes.map((node): ReactFlowNode => {
        const executionNode = flowRun.data.blocks[node.id];
        if (!executionNode) {
          return node;
        }
        if (!isBlockNode(node)) {
          return node;
        }
        return {
          ...node,
          data: {
            ...node.data,
            run: {
              state: executionNode.state,
              returnValue: executionNode.data,
              metadata: executionNode.metadata,
              error: executionNode.error,
              dataSizeBytes: executionNode.dataSizeBytes,
              startedAt: executionNode.startedAt,
              endedAt: executionNode.endedAt,
            },
          },
        };
      }),
      edges,
    };
  }, [reactFlowNodesAndEdges, flowRun]);

  const reRunFlowMutation = useMutation({
    mutationFn: async (args: {
      flowRunId: string;
      versionTarget: api.FlowExecution.FlowReRunVersionTarget;
    }) => {
      return await api.client.FlowExecution.reRunByFlowRunId(args.flowRunId, args.versionTarget);
    },
    onSuccess: () => {
      toast.showSuccess('Restarted Flow Execution');
    },
    onError: (error) => {
      toast.showError(parseError(error, 'Unable to restart flow execution'));
    },
  });

  return {
    flowRun,
    nodes,
    edges,
    reRunFlowMutation,
    flowRunFollowMode,
    setFlowRunFollowMode,
  };
}

export const [FlowRunProvider, useFlowRunContext] = constate(useFlowRun);
